import React, { useMemo, useState, useCallback, useEffect } from 'react';
import HomeView from '../components/HomeView';
import { useAuth } from '../components/AuthView';
import { useMaestroStore } from '../store/maestroStore';
import { useFavorites, loadFavorites } from '../lib/useUserData';
import { isAboveCD, getQualityTier } from '../hooks/useAudioQuality';
import useAppNavigation from '../hooks/useAppNavigation';
import { useRecentAlbumsStore } from '../store/recentAlbumsStore';
import { maybeWarmHomeLibraryIndex } from '../services/homeLibraryIndexWarm';

export default function HomePage() {
  const auth = useAuth();
  const playlists = useMaestroStore((s) => s.playlists);
  const { favorites, toggleFavorite } = useFavorites();
  const nav = useAppNavigation();
  const [, forceRender] = useState(0);

  const recentAlbums = useRecentAlbumsStore((s) => s.albums);
  const fetchRecentAlbums = useRecentAlbumsStore((s) => s.fetchRecent);

  useEffect(() => {
    fetchRecentAlbums();
  }, [fetchRecentAlbums]);

  // Background warm — chunked index load; never blocks Home paint or album opens.
  useEffect(() => {
    maybeWarmHomeLibraryIndex();
  }, []);

  // Warm the Qobuz route chunk while Home is idle so the first navigation feels instant.
  useEffect(() => {
    const preload = () => {
      import('./QobuzPage').catch(() => {});
    };
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(preload, { timeout: 2500 });
      return () => window.cancelIdleCallback?.(id);
    }
    const timer = setTimeout(preload, 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handler = () => loadFavorites();
    window.addEventListener('maestro:favorites-updated', handler);
    return () => window.removeEventListener('maestro:favorites-updated', handler);
  }, []);

  const favAlbums = useMemo(() => {
    const out = [];
    const seen = new Set();

    function stripPrefix(id) {
      if (!id) return id;
      return String(id).replace(/^qobuz:(album|track|artist):/, '');
    }
    function albumDedupKey(fav) {
      const m = fav.metadata || {};
      const nAlbumId = stripPrefix(m.albumId);
      const nItemId = stripPrefix(fav.item_id);
      if (nAlbumId) return `aid:${nAlbumId}`;
      if (fav.item_type === 'album') return `aid:${nItemId}`;
      const title = (m.albumTitle || m.title || '').toLowerCase().trim();
      const artist = (m.artistName || m.artist || '').toLowerCase().trim();
      if (title && artist) return `ta:${title}|${artist}`;
      return `id:${nItemId}`;
    }

    favorites.forEach((fav) => {
      if (!fav.metadata) return;
      const m = fav.metadata;
      const dedupKey = albumDedupKey(fav);
      if (seen.has(dedupKey)) return;
      seen.add(dedupKey);

      const albumId = stripPrefix(
        fav.item_type === 'album' ? fav.item_id : (m.albumId || fav.item_id)
      );
      const title = fav.item_type === 'album'
        ? (m.title || m.albumTitle || '')
        : (m.albumTitle || m.title || '');

      out.push({
        id: albumId,
        serviceAlbumId: albumId,
        title,
        artistName: m.artistName || m.artist || '',
        cover: m.coverUrl || m.cover || m.artworkUrl || m.albumArt || null,
        hires: isAboveCD(m.maxBitDepth, m.maxSampleRate),
        qualityTier: getQualityTier(m.maxBitDepth, m.maxSampleRate),
        source: fav.source || 'qobuz',
        favoritedAt: m.favoritedAt || fav.favoritedAt || null,
        isTrack: fav.item_type === 'track',
        trackId: fav.item_type === 'track' ? fav.item_id : undefined,
        _favItemId: fav.item_id,
        _favItemType: fav.item_type,
        _favSource: fav.source || 'qobuz',
      });
    });
    return out;
  }, [favorites]);

  const handleRemoveFavorite = useCallback((album) => {
    const itemId = album._favItemId || album.id;
    const itemType = album._favItemType || 'album';
    const source = album._favSource || 'qobuz';
    toggleFavorite(itemType, itemId, source);
  }, [toggleFavorite]);

  return (
    <HomeView
      user={auth.user || null}
      counts={{ artists: 0, albums: 0, tracks: 0, composers: 0 }}
      recentAlbums={recentAlbums}
      recentArtists={[]}
      playlists={playlists}
      library={favAlbums}
      onOpenAlbum={nav.openAlbum}
      onOpenArtist={nav.openArtist}
      onOpenPlaylist={nav.openPlaylist}
      onNavigateLibraryTab={(tab) => nav.goLibrary(tab)}
      onRemoveFavorite={handleRemoveFavorite}
    />
  );
}
